import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, FeatureFlag, FlagDataType } from '../api/client';

// ─── Constants ────────────────────────────────────────────────────────────────
const DATA_TYPES: FlagDataType[] = ['boolean', 'string', 'number', 'json'];

const TYPE_CONFIG: Record<FlagDataType, { bg: string; text: string; label: string }> = {
  boolean: { bg: '#dbeafe', text: '#1d4ed8', label: 'Boolean' },
  string:  { bg: '#d1fae5', text: '#065f46', label: 'String'  },
  number:  { bg: '#fef3c7', text: '#92400e', label: 'Number'  },
  json:    { bg: '#ede9fe', text: '#5b21b6', label: 'JSON'    },
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconPlus   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconEdit   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IconRefresh = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>;

// ─── Value preview ────────────────────────────────────────────────────────────
function ValuePreview({ value, dataType }: { value: string; dataType: FlagDataType }) {
  if (dataType === 'boolean') {
    const on = value.toLowerCase() === 'true';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: 12, color: on ? '#16a34a' : '#dc2626' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: on ? '#22c55e' : '#ef4444', display: 'inline-block' }} />
        {on ? 'true' : 'false'}
      </span>
    );
  }
  if (dataType === 'json') {
    return (
      <code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#475569', maxWidth: 260, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        {value}
      </code>
    );
  }
  return <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>;
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
        background: checked ? '#2563eb' : '#cbd5e1',
        position: 'relative', transition: 'background .2s', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        display: 'block',
      }} />
    </button>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
interface ModalProps {
  flag:     Partial<FeatureFlag> | null;
  onClose:  () => void;
  onSaved:  (flag: FeatureFlag) => void;
}

const FlagModal: React.FC<ModalProps> = ({ flag, onClose, onSaved }) => {
  const isEdit = !!flag?.id;

  const [key,         setKey]         = useState(flag?.key         ?? '');
  const [value,       setValue]       = useState(flag?.value       ?? 'true');
  const [dataType,    setDataType]    = useState<FlagDataType>(flag?.dataType ?? 'boolean');
  const [description, setDescription] = useState(flag?.description ?? '');
  const [enabled,     setEnabled]     = useState(flag?.enabled     ?? true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  // Preset value when switching dataType
  const handleTypeChange = (t: FlagDataType) => {
    setDataType(t);
    if (t === 'boolean') setValue('true');
    else if (t === 'number') setValue('0');
    else if (t === 'json') setValue('{}');
    else setValue('');
  };

  const handleSave = async () => {
    if (!key.trim()) { setError('Key is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { key: key.trim(), value, dataType, description: description || null, enabled };
      const saved = isEdit
        ? await apiPut<FeatureFlag>(`/api/feature-flags/${flag!.id}`, body)
        : await apiPost<FeatureFlag>('/api/feature-flags', body);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
            {isEdit ? 'Edit Flag' : 'New Feature Flag'}
          </h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626' }}>{error}</div>
          )}

          {/* Key */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Key</label>
            <input
              className="input-base"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g. ai_analysis_enabled"
              style={{ fontFamily: 'monospace', fontSize: 13 }}
              disabled={isEdit}
            />
            {isEdit && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>Key cannot be changed after creation.</p>}
          </div>

          {/* Data Type */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Data Type</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {DATA_TYPES.map((t) => {
                const cfg = TYPE_CONFIG[t];
                const active = dataType === t;
                return (
                  <button
                    key={t}
                    onClick={() => handleTypeChange(t)}
                    style={{
                      flex: 1, padding: '6px 4px', border: active ? `2px solid ${cfg.text}` : '2px solid #e2e8f0',
                      borderRadius: 7, background: active ? cfg.bg : '#f8fafc',
                      color: active ? cfg.text : '#64748b',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Value */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Value</label>
            {dataType === 'boolean' ? (
              <div style={{ display: 'flex', gap: 8 }}>
                {['true', 'false'].map((v) => (
                  <button
                    key={v}
                    onClick={() => setValue(v)}
                    style={{
                      flex: 1, padding: '8px', border: value === v ? `2px solid ${v === 'true' ? '#16a34a' : '#dc2626'}` : '2px solid #e2e8f0',
                      borderRadius: 7, background: value === v ? (v === 'true' ? '#dcfce7' : '#fee2e2') : '#f8fafc',
                      color: value === v ? (v === 'true' ? '#15803d' : '#991b1b') : '#64748b',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            ) : dataType === 'json' ? (
              <textarea
                className="input-base"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder='{"key": "value"}'
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
              />
            ) : (
              <input
                className="input-base"
                type={dataType === 'number' ? 'number' : 'text'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={dataType === 'number' ? '0' : 'Enter value…'}
              />
            )}
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Description <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <input
              className="input-base"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this flag control?"
            />
          </div>

          {/* Enabled */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Enabled</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Flag is active and readable by the service</div>
            </div>
            <Toggle checked={enabled} onChange={setEnabled} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" onClick={onClose} style={{ padding: '7px 16px', fontSize: 13 }}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '7px 20px', fontSize: 13 }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const FeatureFlags: React.FC = () => {
  const [flags,   setFlags]   = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [modal,   setModal]   = useState<Partial<FeatureFlag> | null | false>(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    apiGet<FeatureFlag[]>('/api/feature-flags')
      .then(setFlags)
      .catch((err) => setError(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaved = (saved: FeatureFlag) => {
    setFlags((prev) => {
      const idx = prev.findIndex((f) => f.id === saved.id);
      return idx >= 0 ? prev.map((f, i) => i === idx ? saved : f) : [saved, ...prev];
    });
    setModal(false);
  };

  const handleToggle = async (flag: FeatureFlag) => {
    try {
      const updated = await apiPut<FeatureFlag>(`/api/feature-flags/${flag.id}`, { enabled: !flag.enabled });
      setFlags((prev) => prev.map((f) => f.id === updated.id ? updated : f));
    } catch { /* keep UI unchanged on failure */ }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this flag? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await apiDelete(`/api/feature-flags/${id}`);
      setFlags((prev) => prev.filter((f) => f.id !== id));
    } catch { /* ignore */ } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ padding: '28px 32px', height: '100vh', overflowY: 'auto', boxSizing: 'border-box' }} className="anim-fadeUp">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>Feature Flags</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-3)' }}>
            Manage runtime feature flags. Changes take effect within 30 seconds.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={load} style={{ padding: '7px 12px' }} title="Refresh"><IconRefresh /></button>
          <button className="btn-primary" onClick={() => setModal({})} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', fontSize: 13 }}>
            <IconPlus /> New Flag
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 52, borderRadius: 8 }} />
            ))}
          </div>
        ) : flags.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center', color: 'var(--text-3)' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🚩</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-2)', marginBottom: 6 }}>No feature flags yet</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>Create your first flag to control features at runtime.</div>
            <button className="btn-primary" onClick={() => setModal({})} style={{ fontSize: 13 }}>
              <IconPlus /> Create Flag
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
                {['Key', 'Type', 'Value', 'Description', 'Enabled', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {flags.map((flag, i) => {
                const tc = TYPE_CONFIG[flag.dataType] ?? TYPE_CONFIG.string;
                return (
                  <tr
                    key={flag.id}
                    style={{ borderBottom: i < flags.length - 1 ? '1px solid var(--border)' : 'none', opacity: flag.enabled ? 1 : 0.55 }}
                  >
                    {/* Key */}
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      <code style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', background: '#f1f5f9', padding: '3px 8px', borderRadius: 5 }}>
                        {flag.key}
                      </code>
                    </td>

                    {/* Type */}
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ background: tc.bg, color: tc.text, borderRadius: 5, padding: '3px 9px', fontSize: 11, fontWeight: 700 }}>
                        {tc.label}
                      </span>
                    </td>

                    {/* Value */}
                    <td style={{ padding: '13px 16px', maxWidth: 240 }}>
                      <ValuePreview value={flag.value} dataType={flag.dataType} />
                    </td>

                    {/* Description */}
                    <td style={{ padding: '13px 16px', color: 'var(--text-3)', fontSize: 13, maxWidth: 280 }}>
                      {flag.description || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>

                    {/* Toggle */}
                    <td style={{ padding: '13px 16px' }}>
                      <Toggle checked={flag.enabled} onChange={() => handleToggle(flag)} />
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn-ghost"
                          onClick={() => setModal(flag)}
                          style={{ padding: '5px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <IconEdit /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(flag.id)}
                          disabled={deleting === flag.id}
                          style={{
                            padding: '5px 10px', fontSize: 12, border: 'none', borderRadius: 'var(--radius-sm)',
                            background: 'transparent', color: '#ef4444', cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            opacity: deleting === flag.id ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <IconTrash /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Stats footer */}
      {!loading && flags.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)', display: 'flex', gap: 16 }}>
          <span>{flags.length} flag{flags.length !== 1 ? 's' : ''} total</span>
          <span>{flags.filter((f) => f.enabled).length} enabled</span>
          {Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
            const count = flags.filter((f) => f.dataType === type).length;
            return count > 0 ? (
              <span key={type} style={{ color: cfg.text }}>{count} {cfg.label.toLowerCase()}</span>
            ) : null;
          })}
        </div>
      )}

      {/* Modal */}
      {modal !== false && (
        <FlagModal
          flag={modal}
          onClose={() => setModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default FeatureFlags;
